import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calculator, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';

const OptionsTradingPage = () => {
  const { api, user } = useAuth();
  const [chain, setChain] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedStock, setSelectedStock] = useState('');
  const [loadingChain, setLoadingChain] = useState(false);
  const [activeTab, setActiveTab] = useState('chain');

  const [tradeForm, setTradeForm] = useState({
    symbol: '',
    optionType: 'call',
    strikePrice: '',
    expirationDate: '',
    contracts: 1
  });

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      const res = await api.get('/options/positions');
      setPositions(res.data);
    } catch (error) {
      console.error('Failed to load positions:', error);
    }
  };

  const loadChain = async () => {
    if (!selectedStock) return;
    setLoadingChain(true);
    try {
      const res = await api.get(`/options/chain/${selectedStock}`);
      setChain(res.data);
    } catch (error) {
      console.error('Failed to load options chain:', error);
    } finally {
      setLoadingChain(false);
    }
  };

  const placeTrade = async (option) => {
    setTradeForm({
      symbol: selectedStock,
      optionType: option.type,
      strikePrice: option.strike,
      expirationDate: option.expiration,
      contracts: 1
    });
  };

  const executeTrade = async () => {
    try {
      await api.post('/options/buy', tradeForm);
      loadPositions();
      alert('Options purchased successfully!');
      setTradeForm({ symbol: '', optionType: 'call', strikePrice: '', expirationDate: '', contracts: 1 });
    } catch (error) {
      console.error('Trade failed:', error);
      alert(error.response?.data?.error || 'Trade failed');
    }
  };

  const closePosition = async (positionId) => {
    try {
      await api.post(`/options/sell/${positionId}`);
      loadPositions();
      alert('Position closed');
    } catch (error) {
      console.error('Close failed:', error);
      alert(error.response?.data?.error || 'Failed to close position');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  if (user?.tier === 'free') {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Card className="bg-white">
            <CardContent className="p-8">
              <Calculator className="w-16 h-16 mx-auto mb-4 text-[#1A4D2E]" />
              <h1 className="font-serif text-2xl text-[#1F2937] mb-4">Options Trading Requires Pro</h1>
              <p className="text-gray-600 mb-6">
                Upgrade to Pro to access options trading with calls, puts, and real-time Greeks.
              </p>
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]">Upgrade to Pro</Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl text-[#1F2937]">Options Trading</h1>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Pro Feature
          </Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="chain">Options Chain</TabsTrigger>
            <TabsTrigger value="positions">My Positions</TabsTrigger>
            <TabsTrigger value="trade">Trade</TabsTrigger>
          </TabsList>

          <TabsContent value="chain">
            <Card>
              <CardHeader>
                <CardTitle>Options Chain</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter stock symbol (e.g., AAPL)"
                    value={selectedStock}
                    onChange={e => setSelectedStock(e.target.value.toUpperCase())}
                    className="max-w-xs"
                  />
                  <Button onClick={loadChain} disabled={loadingChain}>
                    {loadingChain ? 'Loading...' : 'Load Chain'}
                  </Button>
                </div>

                {chain.length > 0 && (
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Type</th>
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Strike</th>
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Expiry</th>
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Bid</th>
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Ask</th>
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Delta</th>
                          <th className="text-left py-3 text-sm font-medium text-gray-600">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chain.slice(0, 50).map((option, i) => (
                          <tr key={i} className="border-b border-stone-100">
                            <td className="py-3">
                              <Badge variant={option.type === 'call' ? 'default' : 'destructive'}>
                                {option.type.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="py-3">{formatCurrency(option.strike)}</td>
                            <td className="py-3 text-sm">{option.expiration}</td>
                            <td className="py-3 text-green-600">{formatCurrency(option.bid)}</td>
                            <td className="py-3 text-red-600">{formatCurrency(option.ask)}</td>
                            <td className="py-3">{option.delta}</td>
                            <td className="py-3">
                              <Button size="sm" variant="outline" onClick={() => placeTrade(option)}>
                                Buy
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {chain.length === 0 && !loadingChain && (
                  <div className="text-center py-12 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Enter a stock symbol to view options chain</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="positions">
            <Card>
              <CardHeader>
                <CardTitle>Your Options Positions</CardTitle>
              </CardHeader>
              <CardContent>
                {positions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {positions.map((pos) => (
                      <div key={pos.id} className="border border-stone-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-lg">
                            {pos.symbol} {pos.optionType.toUpperCase()}
                          </span>
                          <Badge>{pos.contracts} contracts</Badge>
                        </div>
                        <p className="text-sm text-gray-600">
                          Strike: {formatCurrency(pos.strikePrice)} | Exp: {pos.expirationDate}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Cost: {formatCurrency(pos.avgCost)} × 100 = {formatCurrency(pos.avgCost * 100)}/contract
                        </p>
                        <div className="mt-4 pt-4 border-t border-stone-100 space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Market Value</span>
                            <span className="font-medium">{formatCurrency(pos.marketValue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Return</span>
                            <span className={`font-medium ${
                              pos.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {pos.totalReturn >= 0 ? '+' : ''}{formatCurrency(pos.totalReturn)}
                              ({pos.returnPercent >= 0 ? '+' : ''}{pos.returnPercent?.toFixed(2)}%)
                            </span>
                          </div>
                          {pos.greeks && (
                            <div className="text-xs text-gray-500 mt-2">
                              Δ {pos.greeks.delta} | Γ {pos.greeks.gamma} | Θ {pos.greeks.theta}
                            </div>
                          )}
                        </div>
                        <Button
                          className="w-full mt-4"
                          variant="outline"
                          onClick={() => closePosition(pos.id)}
                        >
                          Close Position
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No options positions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trade">
            <Card>
              <CardHeader>
                <CardTitle>Place Trade</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Symbol</label>
                    <Input
                      value={tradeForm.symbol}
                      onChange={e => setTradeForm({...tradeForm, symbol: e.target.value.toUpperCase()})}
                      placeholder="AAPL"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Type</label>
                    <select
                      className="w-full h-11 px-3 border border-stone-200 rounded-lg"
                      value={tradeForm.optionType}
                      onChange={e => setTradeForm({...tradeForm, optionType: e.target.value})}
                    >
                      <option value="call">Call</option>
                      <option value="put">Put</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Strike Price</label>
                    <Input
                      type="number"
                      value={tradeForm.strikePrice}
                      onChange={e => setTradeForm({...tradeForm, strikePrice: e.target.value})}
                      placeholder="150.00"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Expiration</label>
                    <Input
                      type="date"
                      value={tradeForm.expirationDate}
                      onChange={e => setTradeForm({...tradeForm, expirationDate: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Contracts</label>
                    <Input
                      type="number"
                      value={tradeForm.contracts}
                      onChange={e => setTradeForm({...tradeForm, contracts: parseInt(e.target.value)})}
                      min="1"
                    />
                  </div>
                </div>

                {tradeForm.symbol && tradeForm.strikePrice && tradeForm.contracts && (
                  <div className="bg-stone-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Estimated Cost</p>
                    <p className="text-2xl font-bold text-[#1A4D2E]">
                      {formatCurrency(tradeForm.strikePrice * tradeForm.contracts * 100)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {tradeForm.contracts} × {formatCurrency(tradeForm.strikePrice)} × 100 shares
                    </p>
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={executeTrade}
                  disabled={!tradeForm.symbol || !tradeForm.strikePrice || !tradeForm.contracts}
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Buy Options
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default OptionsTradingPage;
